import { RvCancelledReason, RvIncompleteReason, RvProtectionStrategy, RvUserType } from "src/pages/contract/constants";
import { CommonFacilityType } from "../building/building-types";
import { PriceModel } from "../product/product-types";
import { UnionServiceType } from "../public-types";

export interface ReservationPeriodParams {
  serviceId: string; //서비스ID
  serviceType: UnionServiceType; //서비스타입
  commonFacilityType: CommonFacilityType;
  buildingCommonFacilityId: string; // 건물 공용공간 ID
}

export interface ReservationTimesParams extends Omit<ReservationPeriodParams, "date" | "providerId"> {}

// 휴무일 타입 > 하루 전체가 휴무인 날짜 (캘린더 표시용)
export type ClosedDay = {
  date: string;
  isClosed: boolean;
};

// 예약기간 response
export type ReservationPeriodModel = {
  startDate: string;
  endDate: string;
  maxPeriodDays: number;
};

export interface QuotationRequest {
  token: string;
  siteKey?: string;
  quotationItemList: Quotaion[];
}

export interface Quotaion {
  productId: string; //상품 id
  buildingCommonFacilityId: string; //공용공간 id
  startDate: string; //시작일시
  endDate: string; //종료일시
}

export interface QuotationModel {
  id: string; // 견적서 id
  amount: string; //총액
  quotationNo: string; //CUID2 로 생성된 견적서의 Unique Key
  quotationItemList: QuotationItem[]; //"견적 아이템 목록 연속된 시간끼리 하나의 item 으로 묶은 리스트를 전달"
  createdBy: string; //[조회전용] 등록자 ID
  createdDate: string; //[조회전용] 등록일시
  modifiedBy: string; //[조회전용] 수정자 ID
  modifiedDate: string; //[조회전용] 수정일시
}

export interface QuotationItem {
  id: string; // 견적서 item id
  quotationId: string; //견적서 id
  price: number; //아이템별 가격
  productId: string; //상품 id
  buildingCommonFacilityId: string; //공용공간 id
  startDate: string; //시작일시
  endDate: string; //종료일시
  quotationItemPrice: QuotationItemPrice[]; // [조회전용] 견적 아이템 가격 목록(로그정보)
}

export interface QuotationItemPrice {
  id: string; //견적서 아이템 요금 ID
  quotationItemId: string; //견적서 아이템 ID
  priceRuleId: string; //요금 룰 ID
  startDate: string; //시작일시
  endDate: string; //종료일시
  price: string; //판매가
  cost: string; //단가
}

// 휴무기간 조회 api 적용 했을 때 타입
// export type ScheduleData = HolidayPeriodModel & { price?: number };

// slot 이 선택 불가(gray)로 처리된 사유
export type ClosedReason = "OUT_OF_SALE_PERIOD" | "PAST" | "RESERVED" | "CLOSED_PERIOD" | "OUT_OF_PERIOD";

// price 조회 api만 적용했을 때
// closedReasons 는 피드백 우선순위 순으로 정렬 / closedTitle 은 휴무 기간에 해당할 때의 휴무 사유
export type ScheduleData = PriceModel & { isClosed?: boolean; closedReasons?: ClosedReason[]; closedTitle?: string };

export interface QuotationParams {
  id?: number;
  token?: string;
  siteKey?: string;
  quotationNo: string;
}

export interface AcknowledgeReservation {
  id: string | number; // 예약 아이디
  summary?: string; // 예약 제목
  description?: string; // 예약 상세 설명
  start: string; // 예약 시작시간
  end: string; // 예약 종료 시간
  status: ReservationStatus; // 예약 상태정보
  pending: boolean; // 예약 처리 중 여부
  contract: ReservationContract; // 계약정보
  facility: ReservationFacility; // 공용공간 정보
  parentId?: string | number; // 부모 아이디 (그룹 아이디)
  organizer: ReservationOrganizer; // 주최자 정보
  attendees?: ReservationAttendee[]; // 참석자 목록
  incompleteReasons?: RvIncompleteReason[]; // 불완전 예약 사유 목록
  cancelledRemarks?: RvCancelledReason[]; // 취소 예약 비고 목록
  calendarEvent?: ReservationCalendarEvent; // 연동 캘린더 이벤트 정보
  createdBy: ReservationUser; // 생성자 정보
  config?: ReservationOrganizerConfig; // 예약 정보 공개 설정
}

export interface ReservationStatus {
  code: "NONE" | "ACCEPTED" | "ACKNOWLEDGED" | "INCOMPLETE" | "CANCELLED" | "";
  time: string;
}

/** 예약 공용공간 */
export interface ReservationFacility {
  id: string | number; // 공용 공간 아이디
  type: CommonFacilityType; // 공용 공간 타입
  name: string; // 공용 공간 이름
  resources?: string[]; // 공용 시설 기자재 목록
  floor: number; // 위치(층) 음수: 이면 지하, 양수 이면 지상
  maxPeopleCapacity: number; // 시설 정원 수
  primaryImageUrl?: string; // 대표 이미지 공개 URL
  building: ReservationBuilding; // 건물 정보
  deskGroup?: ReservationDeskGroup; // 좌석 그룹 정보
  locationCode?: string; // 로케이션 코드 (이전 버전 호환용)
}

export type ReservationBuilding = {
  id: string | number; // 건물 id
  name: string; // 건물 이름
};

export type ReservationDeskGroup = {
  id: string | number; // 좌석 그룹 아이디
  name: string; // 좌석 그룹 이름
  description?: string; // 좌석 그룹 설명
};

export type ReservationContract = {
  id: string | number; // 계약 아이디(contractId)
  manageId?: string | number; // 계약 관리 아이디(contractManageId)
  applyNumber?: string; // 계약 신청 번호(contractApplyNumber)
};

export type ReservationOrganizer = {
  // 주최자 정보 타입 정의
  memberNo: string;
  userEmail: string;
  displayName?: string;
  phoneNumber: string;
};

export enum RvReservationStatusCode {
  /** 없음 */
  NONE = "NONE",
  /** 접수 */
  ACCEPTED = "ACCEPTED",
  /** 점유 */
  ACKNOWLEDGED = "ACKNOWLEDGED",
  /** 불완전 */
  INCOMPLETE = "INCOMPLETE",
  /** 취소 */
  CANCELLED = "CANCELLED",
}

/** TRV11 예약 항목 (ReservationModel 보다 좁은 응답) */
export type ContractReservationDto = {
  id: string | number; // 예약 아이디
  start: string; // 예약 시작 시간
  end: string; // 예약 종료 시간
  status: ReservationStatus; // 예약 상태 정보
  pending: boolean; // 예약 처리 중 여부
  contract: ReservationContract; // 계약 정보
  facility: Pick<ReservationFacility, "id" | "type" | "name" | "floor" | "building" | "locationCode">; // 공용 공간 정보
  organizer: ReservationOrganizer; // 주최자 정보
};

/** TRV11 응답 data. 건수는 content.length 가 아니라 meta.pageMeta.totalElements 를 볼 것 */
export type ContractReservationListResponseData = {
  content?: ContractReservationDto[]; // 예약 목록 (현재 페이지 분량)
};

/** TRV12 예약 취소 응답 data */
export type ContractReservationCancelResponseData = {
  reservation?: ContractReservationDto; // 취소된 예약 정보 (status.code = CANCELLED)
};

export type ReservationAttendee = {
  // 참석자 정보 타입 정의
  memberNo?: string;
  userEmail?: string;
  displayName?: string;
  responseStatus: "NONE" | "ACCEPTED" | "DECLINED" | "TENTATIVE";
};

export type ReservationCancelledRemark = {
  // 취소 예약 비고 타입 정의
  code: string;
  message: string;
  time: string;
};

export type ReservationCalendarEvent = {
  // 연동 캘린더 이벤트 정보 타입 정의
  id: string; //캘린더 이벤트 아이디
  service: "GOOGLE" | "OUTLOOK";
};

export type ReservationUser = {
  name: string; // 사용자 이름
  type: RvUserType; // 사용자 타입
  remoteAddress: string; // 클라이언트 주소
};

export type ReservationOrganizerConfig = {
  summaryProtectionStrategy: RvProtectionStrategy; //주최자 닉네임 공개 설정
  organizerDisplayNameProtectionStrategy: RvProtectionStrategy;
  organizationNameProtectionStrategy: RvProtectionStrategy;
};

export type ReservationQueryParams = {
  page?: number;
  size?: number;
  id?: string | number; //예약 아이디
  contractManageId?: string | number; //계약관리 id
  buildingId?: string | number; //건물 id
  facilityId?: string | number; // 공용공간 id
  facilityType?: CommonFacilityType; //공용공간 타입
  start?: string; // 검색 시작 시간  URL 인코딩  yyyy-MM-dd'T'HH:mm:ss.SSS+HH:mm  yyyy-MM-dd'T'HH:mm:ss.SSSZ
  end?: string; // 검색 종료 시간
  statusCode?: string;
  greaterThanEqualsEnd?: string; //예약 종료 시간이 비교 하고자 하는 시간과 같거나, 미래인 경우
  organizerOnly?: boolean; //로그인 사용자가 주최자인 예약만 검색 여부 default:false (주최자 또는 참석자인 예약목록 검색)
  contractId?: string; // 계약 id로 조회
};
// 2024-02-14T11:30:00+09:00
// 2024-02-14T:00:00:00+09:00

/**TES01. (무인증)예약가능구간 설정 결과 목록 조회 response */
export type AvailableReservations = {
  id: number; // 예약설정 ID
  partnerId: number; // 파트너ID
  serviceId: number; // 서비스ID
  serviceType: UnionServiceType; // 서비스타입
  commonFacilityType: CommonFacilityType; // 공용공간타입
  isDefault: boolean; // 기본설정여부
  buildingCommonFacilityId: number; // 건물-공용공간 ID
  isLimitedAvailableReservationTime: boolean; // 예약가능시간대제한여부
  availableReservationTimeStart: string; // 예약가능시간대시작
  availableReservationTimeEnd: string; // 예약가능시간대종료
};

/**TES02. (무인증)예약시간단위 설정 결과 목록 response */
export type AvailableReserveTimes = {
  id: number; // 예약설정 ID
  partnerId: number; // 파트너ID
  serviceId: number; // 서비스ID
  serviceType: UnionServiceType; // 서비스타입
  commonFacilityType: CommonFacilityType; // 공용공간타입
  isDefault: boolean; // 기본설정여부
  buildingCommonFacilityId: number; // 건물-공용공간 ID
  isLimitedAvailableReservationTime: boolean; // 예약시간단위제한여부
  minimumReservationTime: number; // 최소예약시간
  maximumReservationTime: number; // 최대예약시간
  isLimitedUnitReservationTime?: boolean; // 예약시간단위제한여부
};

// 예약 정책 유형
export type ReservationPolicyType = "start-time-policy" | "usage-limit-policy" | "closing-time-policy";

// 예약 정책 키
export interface ReservationPolicyKey {
  policyType: ReservationPolicyType;
  scope: {
    // 실제 적용된 항목만 내려온다 > facilityId 는 개별 공간 정책, facilityType 은 공간 유형 기본 정책
    productId?: string | number; // 상품 ID
    contractManageId?: string | number; // 계약 관리 ID
    facilityId?: string | number; // 공용 공간 ID
    facilityType?: CommonFacilityType; // 예약 가능한 공용 공간 타입
  };
}

export interface ReservationStartTimePolicy {
  disabled: boolean; // 정책 비활성 여부
  exclusive: boolean; // 개별 공간 정책을 공간 타입 정책보다 우선 적용할지 여부
  startTimes: {
    dayOfWeek: number; // ISO-8601 요일 > 1=월 ~ 7=일
    offsetsInMinutes: number[]; // 자정(00:00) 기준 분 단위 오프셋 (0~1410, 30분 단위)
  }[];
}

// 휴무일 1건 > 기간 + 반복 요일 + 자정 기준 분 단위 휴무 시간
export interface ClosingRecurrence {
  id: string;
  title?: string; // 휴무 사유 > 미입력 가능
  effectiveFrom: string; // 휴무 기간 시작 날짜 (YYYY-MM-DD)
  effectiveTo: string; // 휴무 기간 종료 날짜 (YYYY-MM-DD)
  daysOfWeek: number[]; // ISO-8601 요일 > 1=월 ~ 7=일
  unavailableFrom: number; // 휴무 시작 시간 > 자정 기준 분 (0~1440)
  unavailableUntil: number; // 휴무 종료 시간 > 자정 기준 분 (0~1440)
}

// 휴무일 정책
export interface ClosingTimePolicy {
  exclusive: boolean; // 개별 공간 정책을 공간 타입 정책보다 우선 적용할지 여부
  recurrences?: ClosingRecurrence[]; // 휴무일 목록
}

// policyType 에 대응하는 필드 하나만 내려온다
export interface ReservationPolicyDetails {
  startTimePolicy?: ReservationStartTimePolicy;
  closingTimePolicy?: ClosingTimePolicy;
}

// 예약 정책 response 타입
export interface ReservationPolicyModel {
  id?: string;
  partnerId?: string;
  key: ReservationPolicyKey;
  details: ReservationPolicyDetails;
}

// 공용 공간 별 유효 예약 정책 > 응답 순서는 요청 순서와 무관하므로 facilityId 로 매칭해야 한다
export interface EffectiveFacilityPolicies {
  facilityId: string;
  facilityType: CommonFacilityType;
  policies?: ReservationPolicyModel[];
}
